import { fontPair, matchColor, presetEntry, sameColor } from '@/components/Editor/Shared/designTokens';

// `swatch` picks a [background, text] pair from the map style's palette below; `fonts` is a FONT_PAIRS key.
const PRESET_LOOKS = [
    { name: 'Tengerparti hangulat', labelKey: 'editor.map_preset.seaside_mood', mapStyle: 22, swatch: 0, shape: 6, fonts: 'beach' },
    { name: 'Városi éjszaka', labelKey: 'editor.map_preset.city_night', mapStyle: 21, swatch: 1, shape: 7, fonts: 'elegant' },
    { name: 'Természetes nyugalom', labelKey: 'editor.map_preset.natural_calm', mapStyle: 36, swatch: 2, shape: 5, fonts: 'nordic' },
    { name: 'Modern elegancia', labelKey: 'editor.map_preset.modern_elegance', mapStyle: 28, swatch: 3, fonts: 'minimal' },
    { name: 'Vintage varázs', labelKey: 'editor.map_preset.vintage_charm', mapStyle: 12, swatch: 4, fonts: 'vintage' },
    { name: 'Minimál chic', labelKey: 'editor.map_preset.minimal_chic', mapStyle: 1, swatch: 5, fonts: 'classic' },
    { name: 'Romantikus éj', labelKey: 'editor.map_preset.romantic_night', mapStyle: 15, swatch: 1, shape: 3, fonts: 'romantic' },
    { name: 'Retro nyár', labelKey: 'editor.map_preset.retro_summer', mapStyle: 5, swatch: 4, shape: 4, fonts: 'warmSerif' },
    { name: 'Londoni köd', labelKey: 'editor.map_preset.london_fog', mapStyle: 13, swatch: 1, fonts: 'condensed' },
    { name: 'Arany óra', labelKey: 'editor.map_preset.golden_hour', mapStyle: 34, swatch: 1, fonts: 'garamond' },
    { name: 'Kék lagúna', labelKey: 'editor.map_preset.blue_lagoon', mapStyle: 10, swatch: 0, shape: 2, fonts: 'artDeco' },
    { name: 'Erdő titka', labelKey: 'editor.map_preset.forest_secret', mapStyle: 27, swatch: 2, fonts: 'literary' },
    { name: 'Neon város', labelKey: 'editor.map_preset.neon_city', mapStyle: 11, swatch: 1, fonts: 'neon' },
    { name: 'Csillagjáró', labelKey: 'editor.map_preset.stargazer', mapStyle: 4, swatch: 3, shape: 8, fonts: 'bold' },
    { name: 'Kék villám', labelKey: 'editor.map_preset.blue_lightning', mapStyle: 23, swatch: 0, shape: 9, fonts: 'sporty' },
    { name: 'Homlokzat', labelKey: 'editor.map_preset.facade', mapStyle: 38, swatch: 0, shape: 10, fonts: 'facade' },
    { name: 'Vörös bársony', labelKey: 'editor.map_preset.red_velvet', mapStyle: 3, swatch: 0, shape: 11, fonts: 'velvet' },
    { name: 'Cseresznyevirág', labelKey: 'editor.map_preset.cherry_blossom', mapStyle: 17, swatch: 0, shape: 12, fonts: 'playful' },
    { name: 'Északi fjord', labelKey: 'editor.map_preset.northern_fjord', mapStyle: 7, swatch: 1, shape: 1, fonts: 'slab' },
    { name: 'Trópusi kert', labelKey: 'editor.map_preset.tropical_garden', mapStyle: 29, swatch: 2, fonts: 'soft' },
    { name: 'Tégla utca', labelKey: 'editor.map_preset.brick_street', mapStyle: 24, swatch: 1, fonts: 'poster' },
    { name: 'Púder álom', labelKey: 'editor.map_preset.powder_dream', mapStyle: 14, swatch: 0, fonts: 'dreamy' },
    { name: 'Kézírásos emlék', labelKey: 'editor.map_preset.handwritten_memory', mapStyle: 36, swatch: 4, fonts: 'handwritten' },
    { name: 'Art deco éj', labelKey: 'editor.map_preset.art_deco_night', mapStyle: 21, swatch: 0, fonts: 'display' },
    { name: 'Retro mozi', labelKey: 'editor.map_preset.retro_cinema', mapStyle: 5, swatch: 1, fonts: 'retro' },
    { name: 'Jövő városa', labelKey: 'editor.map_preset.city_of_future', mapStyle: 18, swatch: 2, fonts: 'futuristic' },
    { name: 'Mesekönyv', labelKey: 'editor.map_preset.storybook', mapStyle: 30, swatch: 4, fonts: 'storybook' },
    { name: 'Szerkesztőség', labelKey: 'editor.map_preset.editorial', mapStyle: 26, swatch: 5, fonts: 'editorial' },
];

const FALLBACK_GROUP = 'grayscale';

// Six poster palettes per map family: [background, text], named like the dropdown.
// Colours are taken from the map style itself so the swatches follow the selected theme.
const PALETTES = {
    grayscale: [
        ['#D9E4EA', '#001426'],
        ['#1C1C1C', '#FFFFFF'],
        ['#B5BFA1', '#1F3B2D'],
        ['#1C1C1C', '#C9A96E'],
        ['#FEF7CE', '#5B3A29'],
        ['#FEFEFE', '#001426'],
    ],
    classic: [
        ['#84BDE9', '#1A3344'],
        ['#2C3A4A', '#F4EFE8'],
        ['#A9CD45', '#243014'],
        ['#1C1C1C', '#FFFFFF'],
        ['#E9E0DA', '#5B3A29'],
        ['#FAF6F4', '#001426'],
    ],
    red: [
        ['#6B1E2B', '#FEF7CE'],
        ['#1C1C1C', '#FF2A62'],
        ['#C42323', '#FEF7CE'],
        ['#1C1C1C', '#FFFFFF'],
        ['#FEF7CE', '#6B1E2B'],
        ['#FEFEFE', '#6B1E2B'],
    ],
    darkLines: [
        ['#E6E4DF', '#001426'],
        ['#1C1C1C', '#FFFFFF'],
        ['#B5BFA1', '#1F3B2D'],
        ['#1C1C1C', '#C9A96E'],
        ['#FEF7CE', '#5B3A29'],
        ['#FEFEFE', '#001426'],
    ],
    retro: [
        ['#84BDE9', '#3A241C'],
        ['#5B3A29', '#F7CDA9'],
        ['#C8B850', '#3A3210'],
        ['#1C1C1C', '#F7CDA9'],
        ['#F7CDA9', '#5B3A29'],
        ['#FEFEFE', '#5B3A29'],
    ],
    blue: [
        ['#8AC0C4', '#1A3335'],
        ['#2C4A4C', '#F1EFE8'],
        ['#B2AC83', '#2A2718'],
        ['#1C1C1C', '#FFFFFF'],
        ['#F1EFE8', '#5B3A29'],
        ['#FEFEFE', '#2C4A4C'],
    ],
    invert: [
        ['#E1E1E1', '#000000'],
        ['#000000', '#FFFFFF'],
        ['#B5BFA1', '#1F3B2D'],
        ['#1C1C1C', '#C9A96E'],
        ['#FEF7CE', '#5B3A29'],
        ['#FEFEFE', '#000000'],
    ],
    aqua: [
        ['#91DAEE', '#1F3338'],
        ['#3A4A4A', '#91DAEE'],
        ['#3A4A4A', '#F4F1EC'],
        ['#1C1C1C', '#FFFFFF'],
        ['#E9E0DA', '#3A4A4A'],
        ['#FEFEFE', '#3A4A4A'],
    ],
    glow: [
        ['#00C8C8', '#001818'],
        ['#000040', '#03FFFF'],
        ['#1010FF', '#FEF7CE'],
        ['#121212', '#FFFFFF'],
        ['#FF9600', '#2A1600'],
        ['#FEFEFE', '#000040'],
    ],
    vintage: [
        ['#007A8C', '#FFFFFF'],
        ['#466F75', '#FFF6C8'],
        ['#8AAD55', '#1A240E'],
        ['#1C1C1C', '#E6D060'],
        ['#FFE146', '#3A3210'],
        ['#FFF6C8', '#466F75'],
    ],
    london: [
        ['#4AB8DC', '#01314E'],
        ['#01314E', '#E8EEF3'],
        ['#ADB866', '#2A3010'],
        ['#1C1C1C', '#FFFFFF'],
        ['#E8E4C8', '#01314E'],
        ['#FEFEFE', '#01314E'],
    ],
    ease: [
        ['#FFF5F4', '#8B2A28'],
        ['#8B2A28', '#FFE8E6'],
        ['#C42323', '#FFFFFF'],
        ['#1C1C1C', '#FFFFFF'],
        ['#FFE8E6', '#5B3A29'],
        ['#FEFEFE', '#8B2A28'],
    ],
    nightlife: [
        ['#8723EE', '#FFFFFF'],
        ['#1D0038', '#F4D0D8'],
        ['#670E3C', '#FEF7CE'],
        ['#1C1C1C', '#FFFFFF'],
        ['#F4D0D8', '#5B1A3A'],
        ['#FEFEFE', '#1D0038'],
    ],
    thin: [
        ['#ECEAD8', '#6B1E2B'],
        ['#6B1E2B', '#ECEAD8'],
        ['#C42323', '#FFFFFF'],
        ['#1C1C1C', '#FFFFFF'],
        ['#ECEAD8', '#5B3A29'],
        ['#FEFEFE', '#6B1E2B'],
    ],
    singapore: [
        ['#FFF5F7', '#8B0030'],
        ['#8B0030', '#FFE4EA'],
        ['#DC224D', '#FFFFFF'],
        ['#1C1C1C', '#FFFFFF'],
        ['#FFE4EA', '#6B1E2B'],
        ['#FEFEFE', '#8B0030'],
    ],
    vibrant: [
        ['#46BCEC', '#01415C'],
        ['#016990', '#E5E3DF'],
        ['#C8D7D4', '#1F3B2D'],
        ['#1C1C1C', '#FFFFFF'],
        ['#E5E3DF', '#5B3A29'],
        ['#FEFEFE', '#016990'],
    ],
    vector: [
        ['#F4F2EC', '#000000'],
        ['#000000', '#FFFFFF'],
        ['#B5BFA1', '#1F3B2D'],
        ['#1C1C1C', '#C9A96E'],
        ['#FEF7CE', '#5B3A29'],
        ['#FEFEFE', '#000000'],
    ],
    pedro: [
        ['#E0E2E3', '#2C3F50'],
        ['#2C3F50', '#F2F2F2'],
        ['#5D6D7E', '#F4F1EC'],
        ['#1C1C1C', '#FFFFFF'],
        ['#E9E0DA', '#2C3F50'],
        ['#F2F2F2', '#2C3F50'],
    ],
    hoogeland: [
        ['#364F74', '#F7EABF'],
        ['#1D2838', '#F1E0AD'],
        ['#F7EABF', '#1D2838'],
        ['#1C1C1C', '#F1E0AD'],
        ['#F1E0AD', '#1D2838'],
        ['#FEFEFE', '#1D2838'],
    ],
    navy: [
        ['#7EBBC8', '#152E42'],
        ['#152E42', '#E0E2E3'],
        ['#1A4A52', '#E0E2E3'],
        ['#1C1C1C', '#FFFFFF'],
        ['#E0E2E3', '#152E42'],
        ['#FEFEFE', '#152E42'],
    ],
    neon: [
        ['#0013FF', '#FFFFFF'],
        ['#121212', '#C9A96E'],
        ['#0013FF', '#FEF7CE'],
        ['#121212', '#FFFFFF'],
        ['#C9A96E', '#1C1C1C'],
        ['#FEFEFE', '#121212'],
    ],
    boston: [
        ['#E9E0DA', '#000000'],
        ['#000000', '#FF3B3B'],
        ['#343434', '#FEF7CE'],
        ['#1C1C1C', '#FFFFFF'],
        ['#FEF7CE', '#5B3A29'],
        ['#FEFEFE', '#000000'],
    ],
    assassin: [
        ['#7F8D89', '#1A1E20'],
        ['#24282B', '#D9E0D4'],
        ['#4D6059', '#F4F1EC'],
        ['#1C1C1C', '#C9A96E'],
        ['#D9E0D4', '#2B3638'],
        ['#FEFEFE', '#24282B'],
    ],
    shadesGrey: [
        ['#E6E4DF', '#1A1A1A'],
        ['#1A1A1A', '#E6E4DF'],
        ['#2A2A2A', '#FEF7CE'],
        ['#1C1C1C', '#C9A96E'],
        ['#E6E4DF', '#5B3A29'],
        ['#FEFEFE', '#1A1A1A'],
    ],
    dinosaur: [
        ['#71C8D4', '#16363A'],
        ['#2C5A60', '#F5F5F2'],
        ['#91B65D', '#243014'],
        ['#1C1C1C', '#FFFFFF'],
        ['#F5F5F2', '#5B3A29'],
        ['#FEFEFE', '#2C5A60'],
    ],
    bright: [
        ['#A2DAF2', '#1B4F72'],
        ['#1B4F72', '#F0EFE8'],
        ['#7CB342', '#1F3B2D'],
        ['#1C1C1C', '#FFFFFF'],
        ['#F7F1DF', '#5B3A29'],
        ['#FEFEFE', '#1B4F72'],
    ],
    interface: [
        ['#AEE0F4', '#2A3A42'],
        ['#3D4A52', '#F9F5ED'],
        ['#C9C9C9', '#2A2A2A'],
        ['#1C1C1C', '#FFFFFF'],
        ['#F9F5ED', '#5B3A29'],
        ['#FEFEFE', '#3D4A52'],
    ],
    warm: [
        ['#5BB5D4', '#003044'],
        ['#813033', '#F9DDC5'],
        ['#645C20', '#F9DDC5'],
        ['#1C1C1C', '#F9DDC5'],
        ['#F9DDC5', '#5B3A29'],
        ['#FEFEFE', '#813033'],
    ],
    paleDawn: [
        ['#ACBCC9', '#3A3228'],
        ['#5B3A29', '#F2E5D4'],
        ['#C5DAC6', '#2A3A28'],
        ['#1C1C1C', '#F2E5D4'],
        ['#F2E5D4', '#5B3A29'],
        ['#FBFAF7', '#5B3A29'],
    ],
    architecture: [
        ['#2A6A84', '#FFFFFF'],
        ['#3F4A50', '#F4F1EC'],
        ['#58C66A', '#14331A'],
        ['#1C1C1C', '#DEE66D'],
        ['#DEE66D', '#2A3210'],
        ['#FEFEFE', '#2A5A70'],
    ],
};

const STYLE_GROUP = {
    1: 'grayscale',
    2: 'classic',
    3: 'red',
    4: 'darkLines',
    5: 'retro',
    6: 'grayscale',
    7: 'blue',
    8: 'grayscale',
    9: 'invert',
    10: 'aqua',
    11: 'glow',
    12: 'vintage',
    13: 'london',
    14: 'ease',
    15: 'nightlife',
    16: 'thin',
    17: 'singapore',
    18: 'vibrant',
    19: 'vector',
    20: 'pedro',
    21: 'hoogeland',
    22: 'navy',
    23: 'neon',
    24: 'boston',
    25: 'vibrant',
    26: 'grayscale',
    27: 'assassin',
    28: 'shadesGrey',
    29: 'dinosaur',
    30: 'bright',
    31: 'grayscale',
    32: 'interface',
    33: 'grayscale',
    34: 'warm',
    35: 'bright',
    36: 'paleDawn',
    37: 'bright',
    38: 'architecture',
};

function paletteForStyle(styleId) {
    return PALETTES[STYLE_GROUP[styleId] || FALLBACK_GROUP] || PALETTES[FALLBACK_GROUP];
}

export function buildStylePresets(backgrounds, textColors, mapStyles, shapes) {
    return PRESET_LOOKS.map((look) => {
        const palette = paletteForStyle(look.mapStyle);
        const [backgroundColor, textColor] = palette[look.swatch] || palette[0];
        const options = {
            background: matchColor(backgroundColor, backgrounds, look.name),
            text: matchColor(textColor, textColors),
            mapStyle: mapStyles.find((style) => style.id === look.mapStyle),
            shape: look.shape && shapes ? shapes.find((option) => option.id === look.shape) : null,
            ...fontPair(look.fonts),
        };
        return {
            ...presetEntry(look.mapStyle * 10 + look.swatch, look.name, options, {
                backgroundColor,
                color: textColor,
            }),
            labelKey: look.labelKey,
        };
    });
}

export function presetMatches(preset, backgroundColor, textColor, mapStyleId, titleLabel, messageLabel, shapeId) {
    return (
        sameColor(preset.options.background.color, backgroundColor) &&
        sameColor(preset.options.text.color, textColor) &&
        preset.options.mapStyle &&
        preset.options.mapStyle.id === mapStyleId &&
        preset.options.titleTypo &&
        preset.options.titleTypo.label === titleLabel &&
        preset.options.messageTypo &&
        preset.options.messageTypo.label === messageLabel &&
        (!preset.options.shape || preset.options.shape.id === shapeId)
    );
}
